import React, { useState } from 'react';
import { Check, ArrowRight, Zap, Star, Building2 } from 'lucide-react';

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: '₹0',
    period: 'forever',
    icon: '🆓',
    color: '#64748b',
    gradient: 'linear-gradient(135deg,#f1f5f9,#e2e8f0)',
    btnBg: '#64748b',
    features: [
      '5 Requirements/month',
      '10 Bench Resources',
      '3 Candidate submissions/req',
      'Basic Analytics',
      'Email Support',
    ],
    locked: ['AI Interviews', 'Priority Support', 'Advanced Analytics'],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '₹4,999',
    period: '/month',
    icon: '⚡',
    color: '#6366f1',
    gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
    btnBg: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
    popular: true,
    features: [
      'Unlimited Requirements',
      'Unlimited Bench Resources',
      '10 Candidate submissions/req',
      'Advanced Analytics',
      'AI Interviews (50/month)',
      'Priority Support',
      'Custom Branding',
    ],
    locked: [],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: '₹19,999',
    period: '/month',
    icon: '🏢',
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg,#f59e0b,#d97706)',
    btnBg: 'linear-gradient(135deg,#f59e0b,#d97706)',
    features: [
      'Everything in Pro',
      'Unlimited AI Interviews',
      'Dedicated Account Manager',
      'Custom Integrations',
      'Full API Access',
      '24/7 Priority Support',
      'White Label Option',
    ],
    locked: [],
  },
];

export default function PlanSelection({ user, onSelectPlan }) {
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSelect = (planId) => {
    setSelected(planId);
  };

  const handleContinue = () => {
    if (!selected) return;
    setLoading(true);
    setTimeout(() => {
      localStorage.setItem('sk_plan', selected);
      onSelectPlan(selected);
    }, 800);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', fontFamily: '-apple-system,BlinkMacSystemFont,sans-serif', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '48px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '24px' }}>
          <div style={{ width: '44px', height: '44px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px' }}>⚡</div>
          <span style={{ color: 'white', fontWeight: '800', fontSize: '22px' }}>Skillora</span>
        </div>
        <h1 style={{ color: 'white', fontSize: 'clamp(28px,4vw,40px)', fontWeight: '900', margin: '0 0 12px' }}>
          Welcome, {user.name}! 👋
        </h1>
        <p style={{ color: '#64748b', fontSize: '16px', margin: 0 }}>
          Choose your plan to get started. You can upgrade anytime.
        </p>
      </div>

      {/* Plans */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '20px', maxWidth: '1000px', width: '100%', marginBottom: '32px' }}>
        {PLANS.map(plan => (
          <div key={plan.id} onClick={() => handleSelect(plan.id)}
            style={{ background: selected === plan.id ? 'rgba(99,102,241,0.1)' : 'rgba(255,255,255,0.03)', borderRadius: '16px', padding: '28px', border: selected === plan.id ? '2px solid #6366f1' : plan.popular ? '2px solid rgba(99,102,241,0.3)' : '1px solid rgba(255,255,255,0.06)', cursor: 'pointer', position: 'relative', transition: 'all 0.2s' }}>

            {plan.popular && (
              <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white', padding: '4px 16px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', whiteSpace: 'nowrap' }}>
                ⭐ Most Popular
              </div>
            )}

            {selected === plan.id && (
              <div style={{ position: 'absolute', top: '16px', right: '16px', width: '24px', height: '24px', borderRadius: '50%', background: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Check size={14} color="white" />
              </div>
            )}

            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '36px', marginBottom: '8px' }}>{plan.icon}</div>
              <h3 style={{ color: 'white', fontSize: '20px', fontWeight: '800', margin: '0 0 8px' }}>{plan.name}</h3>
              <div style={{ color: plan.color, fontSize: '32px', fontWeight: '900', marginBottom: '2px' }}>{plan.price}</div>
              <div style={{ color: '#64748b', fontSize: '13px' }}>{plan.period}</div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              {plan.features.map(f => (
                <div key={f} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: 'rgba(34,197,94,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Check size={10} color="#16a34a" />
                  </div>
                  <span style={{ color: '#94a3b8', fontSize: '13px' }}>{f}</span>
                </div>
              ))}
              {plan.locked.map(f => (
                <div key={f} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', opacity: 0.4 }}>
                  <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: 'rgba(100,116,139,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Check size={10} color="#64748b" />
                  </div>
                  <span style={{ color: '#475569', fontSize: '13px', textDecoration: 'line-through' }}>{f}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Continue button */}
      <button onClick={handleContinue} disabled={!selected || loading}
        style={{ padding: '14px 48px', background: selected ? 'linear-gradient(135deg,#6366f1,#8b5cf6)' : 'rgba(255,255,255,0.1)', color: 'white', border: 'none', borderRadius: '10px', cursor: selected ? 'pointer' : 'default', fontSize: '16px', fontWeight: '700', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '8px', opacity: !selected ? 0.5 : 1, boxShadow: selected ? '0 0 30px rgba(99,102,241,0.3)' : 'none' }}>
        {loading ? 'Setting up your account...' : 'Continue to Dashboard'} {!loading && <ArrowRight size={18} />}
      </button>

      <p style={{ color: '#475569', fontSize: '13px', marginTop: '16px' }}>
        ✅ No credit card required for Free plan
      </p>
    </div>
  );
}
